/**
 * AI Suggestion Panel Component
 * 
 * Main panel that displays AI analysis results in a drawer/sidebar.
 * Includes classification, suggested actions, reply draft, and next steps.
 * Implements human-in-the-loop pattern - all actions require user confirmation.
 */

import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Paper,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  TextField,
  Skeleton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Psychology as AiIcon,
  ExpandMore as ExpandIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Info as InfoIcon,
  Lightbulb as TipIcon,
  FormatListBulleted as StepsIcon,
} from '@mui/icons-material';
import AiCategoryBadge, { AiAnalyzingBadge, CATEGORY_CONFIG } from './AiCategoryBadge';
import AiActionButtons from './AiActionButtons';

/**
 * AiSuggestionPanel - Main AI suggestions drawer
 * 
 * @param {boolean} open - Whether the drawer is open
 * @param {function} onClose - Handler to close the drawer
 * @param {Object} email - The email being analyzed
 * @param {Object} analysis - AI analysis result
 * @param {boolean} loading - Whether analysis is in progress
 * @param {string} error - Error message if analysis failed
 * @param {function} onAnalyze - Handler to trigger analysis
 * @param {function} onApplyAction - Handler when action is applied
 * @param {function} onInsertDraft - Handler to insert draft into composer
 * @param {function} onRegenerate - Handler to regenerate analysis
 * @param {function} onUpgradeModel - Handler to use advanced model
 */
export default function AiSuggestionPanel({
  open,
  onClose,
  email,
  analysis,
  loading = false,
  error = null,
  onAnalyze,
  onApplyAction,
  onInsertDraft,
  onRegenerate,
  onUpgradeModel,
}) {
  const [editedDraft, setEditedDraft] = useState('');
  const [isEditingDraft, setIsEditingDraft] = useState(false);
  const [copiedDraft, setCopiedDraft] = useState(false);
  const [appliedActions, setAppliedActions] = useState([]);
  const [expanded, setExpanded] = useState(['actions', 'draft', 'steps']);

  // Reset state when email changes
  useEffect(() => {
    if (email?.id) {
      setEditedDraft('');
      setIsEditingDraft(false);
      setCopiedDraft(false);
      setAppliedActions([]);
    }
  }, [email?.id]);

  // Update draft when analysis changes
  useEffect(() => {
    if (analysis?.reply_draft) {
      setEditedDraft(analysis.reply_draft);
    }
  }, [analysis?.reply_draft]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(prev => 
      isExpanded 
        ? [...prev, panel] 
        : prev.filter(p => p !== panel)
    );
  };

  const handleCopyDraft = async () => {
    const draftToCopy = isEditingDraft ? editedDraft : analysis?.reply_draft;
    if (draftToCopy) {
      await navigator.clipboard.writeText(draftToCopy);
      setCopiedDraft(true);
      setTimeout(() => setCopiedDraft(false), 2000);
    }
  };

  const handleApplyAction = async (action) => {
    if (onApplyAction) {
      const success = await onApplyAction(action);
      if (success !== false) {
        setAppliedActions(prev => [...prev, action]);
      }
    }
  };

  const handleInsertDraft = () => {
    const draftToInsert = isEditingDraft ? editedDraft : analysis?.reply_draft;
    if (onInsertDraft && draftToInsert) {
      onInsertDraft(draftToInsert);
    }
  };

  const drawerWidth = 400;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      variant={isMobile ? 'temporary' : 'persistent'}
      sx={{
        width: isMobile ? '100%' : (open ? drawerWidth : 0),
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isMobile ? '100%' : drawerWidth,
          boxSizing: 'border-box',
          top: isMobile ? 'auto' : 64,
          height: isMobile ? 'auto' : 'calc(100% - 64px)',
          maxHeight: isMobile ? '90vh' : 'none',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AiIcon />
          <Typography variant="h6" fontWeight="bold">
            AI Assistant
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'inherit' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Disclaimer */}
      <Alert 
        severity="info" 
        icon={<InfoIcon fontSize="small" />}
        sx={{ 
          borderRadius: 0, 
          py: 0.5,
          '& .MuiAlert-message': { fontSize: '0.75rem' }
        }}
      >
        I suggerimenti AI richiedono la tua conferma prima di essere applicati.
      </Alert>

      {/* Content */}
      <Box sx={{ overflow: 'auto', flex: 1, p: 2 }}>
        {/* Not analyzed state */}
        {!analysis && !loading && !error && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <AiIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Email non ancora analizzata
            </Typography>
            <Button
              variant="contained"
              startIcon={<AiIcon />}
              onClick={() => onAnalyze()}
              sx={{ mt: 2 }}
            >
              Analizza con AI
            </Button>
          </Box>
        )}

        {/* Loading state */}
        {loading && (
          <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <CircularProgress size={24} />
              <Typography>Analisi in corso...</Typography>
            </Box>
            <Skeleton variant="rounded" height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={100} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={60} />
          </Box>
        )}

        {/* Error state */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Errore Analisi</AlertTitle>
            {error}
            <Button
              size="small"
              onClick={() => onAnalyze()}
              sx={{ mt: 1 }}
            >
              Riprova
            </Button>
          </Alert>
        )}

        {/* Analysis Results */}
        {analysis && !loading && (
          <>
            {/* Classification */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Classificazione
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <AiCategoryBadge
                  category={analysis.category}
                  confidence={analysis.confidence}
                  rationale={analysis.rationale}
                  size="medium"
                />
              </Box>
              {analysis.rationale && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {analysis.rationale}
                </Typography>
              )}
              {analysis.insufficient_context && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Contesto insufficiente per un'analisi completa
                </Alert>
              )}
            </Paper>

            {/* Suggested Actions */}
            <Accordion 
              expanded={expanded.includes('actions')}
              onChange={handleAccordionChange('actions')}
              defaultExpanded
            >
              <AccordionSummary expandIcon={<ExpandIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TipIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight="medium">
                    Azioni Suggerite
                  </Typography>
                  <Chip 
                    size="small" 
                    label={analysis.suggested_actions?.length || 0} 
                    color="primary"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <AiActionButtons
                  suggestedActions={analysis.suggested_actions}
                  onApplyAction={handleApplyAction}
                  onRegenerate={onRegenerate}
                  onUpgradeModel={onUpgradeModel}
                  loading={loading}
                  currentModel={analysis.model || 'non disponibile'}
                  appliedActions={appliedActions}
                />
              </AccordionDetails>
            </Accordion>

            {/* Reply Draft */}
            {analysis.reply_draft && (
              <Accordion 
                expanded={expanded.includes('draft')}
                onChange={handleAccordionChange('draft')}
              >
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EditIcon color="secondary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="medium">
                      Bozza Risposta
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {isEditingDraft ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={8}
                      value={editedDraft}
                      onChange={(e) => setEditedDraft(e.target.value)}
                      variant="outlined"
                      size="small"
                    />
                  ) : (
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        bgcolor: 'grey.50',
                        maxHeight: 200,
                        overflow: 'auto',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {analysis.reply_draft}
                    </Paper>
                  )}
                  
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button
                      size="small"
                      variant={isEditingDraft ? 'outlined' : 'text'}
                      startIcon={<EditIcon />}
                      onClick={() => setIsEditingDraft(!isEditingDraft)}
                    >
                      {isEditingDraft ? 'Fine Modifica' : 'Modifica'}
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={copiedDraft ? <CheckIcon /> : <CopyIcon />}
                      onClick={handleCopyDraft}
                      color={copiedDraft ? 'success' : 'inherit'}
                    >
                      {copiedDraft ? 'Copiato!' : 'Copia'}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={handleInsertDraft}
                    >
                      Inserisci Risposta
                    </Button>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Next Steps */}
            {analysis.next_steps && analysis.next_steps.length > 0 && (
              <Accordion 
                expanded={expanded.includes('steps')}
                onChange={handleAccordionChange('steps')}
              >
                <AccordionSummary expandIcon={<ExpandIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StepsIcon color="info" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="medium">
                      Prossimi Passi
                    </Typography>
                    <Chip 
                      size="small" 
                      label={analysis.next_steps.length} 
                      color="info"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {analysis.next_steps.map((step, index) => (
                      <Paper
                        key={index}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        <Chip
                          size="small"
                          label={index + 1}
                          color="info"
                          sx={{ minWidth: 24, height: 24 }}
                        />
                        <Typography variant="body2">
                          {step}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Suggested Tags */}
            {analysis.tags_suggestion && analysis.tags_suggestion.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Tag Suggeriti
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {analysis.tags_suggestion.map((tag, index) => (
                    <Chip
                      key={index}
                      size="small"
                      label={tag}
                      variant="outlined"
                      onClick={() => handleApplyAction('tag')}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Stack>
              </Paper>
            )}

            {/* Model Info */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
              <Typography variant="caption" color="text.secondary">
                Analizzato con {analysis.model || 'non disponibile'} • {' '}
                {analysis.analyzed_at && new Date(analysis.analyzed_at).toLocaleString('it-IT')}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
}

/**
 * AiSuggestionButton - Floating button to open AI panel
 */
export function AiSuggestionButton({ onClick, hasAnalysis, loading }) {
  return (
    <Button
      variant={hasAnalysis ? 'outlined' : 'contained'}
      color="primary"
      startIcon={loading ? <CircularProgress size={16} /> : <AiIcon />}
      onClick={onClick}
      disabled={loading}
      sx={{
        textTransform: 'none',
        borderRadius: 2,
      }}
    >
      {loading ? 'Analisi...' : hasAnalysis ? 'Vedi Suggerimenti AI' : 'Analizza con AI'}
    </Button>
  );
}
